import React, { FC } from 'react';
import styled from 'styled-components';
import { BodyText, FlexCol, FlexRow, NoticeBlock } from '@minecraft.atoms';
import { MediaTypeCodeEnum } from '@minecraft.graphql-operations';
import { Grid } from '@minecraft.grid';
import Icon from '@minecraft.icon';
import { grid } from '@minecraft.themes';
import { Trans, useTranslation } from '@blocs.i18n';
import { useUploadMediaPreviewDefs } from './useUploadMediaPreviewDefs';
import { MediaUploaderContext } from './types';

const StyledGridWrapper = styled.div`
  width: 100%;
  margin: auto;
  flex-grow: 2;

  .overflow-cell .ag-cell-wrapper {
    max-height: 100%;
    overflow: hidden;
  }

  @media screen and (max-width: ${grid.breakpoints.md}) {
    &.ag-theme-alpine {
      .ag-root-wrapper {
        border: none;
      }

      .ag-header,
      .ag-header-cell,
      .ag-header-viewport {
        font-size: 1rem;
        border-bottom: none;
        margin-bottom: 0.5rem;
      }

      .ag-header-cell-label {
        padding: 0 1rem;
      }

      .ag-row {
        border: none;
      }
    }
  }
`;

const SupportLink: FC = ({ children }) => {
  return (
    <a
      href="https://support.castingnetworks.com/hc/en-us/articles/22862430327181-Talent-Acceptable-Media-File-Types"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};

interface MediaUploaderPreviewProps {
  filesToPreview: MediaUploaderContext['filesToPreview'];
  filesToUpload: MediaUploaderContext['filesToUpload'];
  currentMediaType: MediaTypeCodeEnum;
  onFilesSelected: (files: File[]) => void;
  errMsg?: string;
  creditWarningMsg?: string;
}

export const MediaUploaderPreview = ({
  filesToPreview,
  filesToUpload,
  currentMediaType,
  onFilesSelected,
  errMsg,
  creditWarningMsg,
}: MediaUploaderPreviewProps) => {
  const { t } = useTranslation();
  const columnDef = useUploadMediaPreviewDefs({
    currentMediaType,
    onFilesSelected,
    filesToPreview,
    filesToUpload,
  });
  const hasAnyFailedFiles = filesToPreview.some((file) => file.uploadError);

  return (
    <FlexCol gap="4" data-testid="media-upload-preview-pane" className="cn_w-full">
      {creditWarningMsg && (
        <NoticeBlock data-testid="media-uploader-credit-warning-wrapper" message={creditWarningMsg} />
      )}
      {errMsg && (
        <FlexRow
          gap="4"
          className="cn_w-full cn_atom_p-4"
          borderPlacement="all"
          borderColor="statusRejected"
          borderRadius="sm"
          bgColor="statusRejected"
          wrap="nowrap"
          alignItems="center"
          data-testid="media-uploader-error-wrapper"
        >
          <FlexRow grow={0} shrink={0}>
            <Icon height={1.4} themeColor="danger" name="info-filled" />
          </FlexRow>
          {/* if we have an error message, but not any failed files then the error came from the initialize or finalize graphql calls */}
          {!hasAnyFailedFiles && (
            <FlexRow gap="2">
              <BodyText>{t('common:mediaStorage.error.uploadFailed')}</BodyText>
              <BodyText>{errMsg}</BodyText>
            </FlexRow>
          )}
          {/* if we have failed files then at least one file failed to upload correctly */}
          {hasAnyFailedFiles && (
            <FlexRow gap="2">
              <BodyText data-testid="cloudinary-error-alert-wrapper">
                <Trans i18nKey="common:mediaStorage.error.cloudinaryFailure" components={[<SupportLink />]} />
              </BodyText>
            </FlexRow>
          )}
        </FlexRow>
      )}
      <StyledGridWrapper data-qa-id="media-upload-preview-pane" className="ag-theme-alpine cn_h-120 cn_md:h-72">
        <Grid
          columnDefs={columnDef}
          rowSelection="multiple"
          rowData={filesToPreview}
          getRowId={(row) => row.data.fileName}
          rowHeight={76}
          suppressHorizontalScroll
          suppressRowClickSelection
          onNewColumnsLoaded={({ api }) => {
            api.redrawRows();
            api.refreshHeader();
          }}
        />
        <div className="overscroll-hint" />
      </StyledGridWrapper>
    </FlexCol>
  );
};
